import Pagination from '@mui/material/Pagination';
import FilterBox from 'components/commons/FilterBox';
import TitleCleansingModal from 'containers/TitleCleansingModal';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrderCleansingDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { useStoreWrapper } from 'stores/StoreHelper';
import styled from 'styled-components';
import { usePaginationHeader, PaginationInfo } from 'hooks/usePagenation';
import { SyncLoader } from 'react-spinners';
import { Row } from 'saladsoft-primitive';
import { useTranslation } from 'react-i18next';
import { SearchParams, SettingItems } from 'types/CommonTypes';
import { SortingState } from '@tanstack/react-table';
import { Settings } from '@mui/icons-material';
import { Badge, IconButton } from '@mui/material';
import CleansingColumnSettingsModal from 'containers/CleansingColumnSettingsModal';
import CommonTable from 'components/commonTable/Table';
import { OverlayStyle } from 'resources/styles/LoadingOverlay';

const TitleClensingPage = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { uiStore } = useStoreWrapper();
  const orderCleansingRef = useRef<OrderCleansingDto[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [orderCleansings, setOrderCleansings] = useState<OrderCleansingDto[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  // 모달에서 retry처리에 대한 리로드용

  // 표시/비표시 제어용 컬럼 아이템 설정
  const defaultVisibleColumnItems: SettingItems[] = [
    { id: 'orderId', label: 'Order ID', visible: true, seq: 1 },
    { id: 'trackingNo', label: 'Tracking No', visible: true, seq: 2 },
    { id: 'itemName', label: 'Title(Origin)', visible: true, seq: 3 },
    { id: 'itemOption', label: 'Option', visible: true, seq: 4 },
    { id: 'optionCode', label: 'Option Code', visible: true, seq: 5 },
    { id: 'customsTitle', label: 'Cleansing Title', visible: true, seq: 6 },
    { id: 'iconColumn', label: 'Cleanse Icon', visible: true, seq: 7 },
    { id: 'status', label: 'Status', visible: true, seq: 8 },
    { id: 'hsCode', label: 'HS Code', visible: true, seq: 9 },
    { id: 'subMasterBl', label: 'Sub Master BL', visible: true, seq: 10 },
    { id: 'masterBl', label: 'Master BL', visible: true, seq: 11 },
    { id: 'itemCode', label: 'Item Code', visible: true, seq: 12 },
    { id: 'material', label: 'Material', visible: true, seq: 13 },
    { id: 'itemUrl', label: 'Market', visible: true, seq: 14 },
    { id: 'realWeight', label: 'Weight', visible: true, seq: 15 },
    { id: 'unitPrice', label: 'Unit Price', visible: true, seq: 16 },
    { id: 'quantity', label: 'Quantity', visible: true, seq: 17 },
    { id: 'regDatetime', label: 'Created At', visible: true, seq: 14 },
    { id: 'modifyDate', label: 'Modified At', visible: true, seq: 14 },
  ];

  const [visibleColumnItems, setVisibleColumnItems] = useState<SettingItems[]>([]);
  const [isSettingsChange, setIsSettingsChange] = useState<'ALL_SHOW' | 'EXIST_HIDE' | 'ALL_HIDE'>(
    'ALL_SHOW',
  );

  // 컬럼 표시 제어 저장 클릭시 처리(localStorage에 저장)
  const handleSave = (columnItems: SettingItems[]) => {
    const sortedColumnItems = columnItems.sort((a, b) => (a.seq || 0) - (b.seq || 0));
    localStorage.setItem('cleansingTableColumnSettings', JSON.stringify(sortedColumnItems));

    const isSettingsChangeCheck =
      sortedColumnItems.filter((item) => item.visible !== true).length === 0
        ? 'ALL_SHOW'
        : sortedColumnItems.filter((item) => item.visible === true).length === 0
          ? 'ALL_HIDE'
          : 'EXIST_HIDE';
    setIsSettingsChange(isSettingsChangeCheck);
    setVisibleColumnItems(sortedColumnItems);
  };

  // 초기 제어(표시/비표시 취득)
  useEffect(() => {
    const localStoredItems = localStorage.getItem('cleansingTableColumnSettings');
    if (localStoredItems) {
      // 이미 저장된 아이템이 있는 경우만
      let jsonItems: SettingItems[] = [];
      try {
        jsonItems = JSON.parse(localStoredItems);
        // 만약 추가되거나 삭제되는 컬럼이 있더라도, 기본 설정 컬럼을 바탕으로 재저장하게 처리
        const mergeColumnItems = defaultVisibleColumnItems.map((item) => {
          const storeItem = jsonItems.find((s) => s.id === item.id);
          return {
            ...item,
            visible: storeItem?.visible ?? true,
            seq: storeItem?.seq ?? item.seq,
          };
        });
        const sortedColumnItems = mergeColumnItems.sort((a, b) => (a.seq || 0) - (b.seq || 0));
        localStorage.setItem('cleansingTableColumnSettings', JSON.stringify(sortedColumnItems));
        setVisibleColumnItems(sortedColumnItems);

        const isSettingsChangeCheck =
          sortedColumnItems.filter((item) => item.visible !== true).length === 0
            ? 'ALL_SHOW'
            : sortedColumnItems.filter((item) => item.visible === true).length === 0
              ? 'ALL_HIDE'
              : 'EXIST_HIDE';
        setIsSettingsChange(isSettingsChangeCheck);
      } catch (e) {
        jsonItems = defaultVisibleColumnItems;
        localStorage.setItem('cleansingTableColumnSettings', JSON.stringify(jsonItems));
        setVisibleColumnItems(jsonItems);
        setIsSettingsChange('ALL_SHOW');
      }
    } else {
      // 저장된 아이템이 없다면
      setVisibleColumnItems(defaultVisibleColumnItems);
      setIsSettingsChange('ALL_SHOW');
    }
  }, []);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);

    // 현재 검색 파라미터를 유지하면서 페이지만 업데이트
    const currentParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      currentParams[key] = value;
    });

    setSearchParams({
      ...currentParams,
      page: String(value),
    });
  };

  const handleSearch = (searchParams: SearchParams) => {
    const newSearchParams = { ...searchParams };
    if (newSearchParams.status === 'ALL') {
      delete newSearchParams.status;
    }
    if (Number(newSearchParams.category) === -1) {
      delete newSearchParams.category;
    }

    const params = Object.entries({
      size: String(newSearchParams.size),
      page: '1',
      startDate: newSearchParams.startDate ?? '',
      endDate: newSearchParams.endDate ?? '',
      category: newSearchParams.category ?? '',
      status: newSearchParams.status ?? '',
      searchKey: newSearchParams.searchKey ?? '',
      searchValue: newSearchParams.searchValue ?? '',
      sort: newSearchParams.sort ?? '',
    }).reduce(
      (acc, [key, value]) => {
        if (value !== 'undefined' && value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    setSearchParams(params);
  };

  const handleRowClick = (row: OrderCleansingDto) => {
    let direction;
    const index = orderCleansingRef.current.findIndex((order) => order.id === row.id);
    if (index === orderCleansingRef.current.length - 1) {
      direction = 'next';
    }

    if (index === 0) {
      direction = 'prev';
    }

    uiStore?.modal.show({
      style: {
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        maxHeight: '95%',
        marginLeft: '20px',
        marginRight: '20px',
      },
      title: t('Title Cleansing'),
      children: <TitleCleansingModal row={row} onApply={handleDoneClick} direction={direction} />,
    });
  };

  // Settings 화면의 Modal Width값만 고정, 화면 중간에 위치하게 Margin설정
  const handleSettingsClick = () => {
    uiStore?.modal.show({
      style: {
        width: '400px',
        height: '90%',
        maxHeight: '95%',
        marginLeft: 'auto',
        marginRight: 'auto',
      },
      title: t('Visiblity Items Settings'),
      children: <CleansingColumnSettingsModal items={visibleColumnItems} onSave={handleSave} />,
    });
  };

  const handleDoneClick = (
    id: number,
    clensingTitle: string,
    applyToAll: boolean = false,
    row?: OrderCleansingDto,
  ) => {
    ServiceHelper?.orderCleansingService
      .updateOrderCleansing(id, { customsTitle: clensingTitle, applyToAll })
      .then((res) => {
        if (res.status === 200) {
          const targetIndex = orderCleansingRef.current.findIndex((item) => item.id === id);
          if (targetIndex > -1) {
            const updatedOrderCleansing = {
              ...orderCleansingRef.current[targetIndex],
              customsTitle: clensingTitle,
              status: 'USER_VERIFIED' as const,
            };

            if (applyToAll && row) {
              const updatedOrderCleansings = orderCleansingRef.current.map((item) => {
                if (
                  item.itemName === row.itemName &&
                  item.itemCode === row.itemCode &&
                  item.optionCode === row.optionCode &&
                  item.itemOption === row.itemOption // 옵션명 추가
                ) {
                  return {
                    ...item,
                    customsTitle: clensingTitle,
                    status: 'USER_VERIFIED' as const,
                  };
                }
                return item;
              });
              setOrderCleansings(updatedOrderCleansings);
              orderCleansingRef.current = updatedOrderCleansings;
            } else {
              const updatedOrderCleansings = [
                ...orderCleansingRef.current.slice(0, targetIndex),
                updatedOrderCleansing,
                ...orderCleansingRef.current.slice(targetIndex + 1),
              ];
              setOrderCleansings(updatedOrderCleansings);
              orderCleansingRef.current = updatedOrderCleansings;
            }
          }
        }
      })
      .catch((err) => {
        alert(err.response.data.message);
      });
  };

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);

    if (newSorting.length > 0) {
      const sortId = newSorting[0].id;
      const sortOrder = newSorting[0].desc ? 'desc' : 'asc';
      const sortParam = `${sortId},${sortOrder}`;

      // 현재 검색 파라미터에 정렬 정보 추가
      const currentParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });

      setSearchParams({
        ...currentParams,
        sort: sortParam,
      });
    } else {
      // 정렬이 없는 경우 정렬 파라미터 제거
      const currentParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });
      const { sort, order, ...restParams } = currentParams;
      setSearchParams(restParams);
    }
  };

  const fetchOrderCleansings = (page?: number, searchParams?: SearchParams) => {
    setLoading(true);
    ServiceHelper?.orderCleansingService
      ?.getOrderCleansings({
        page: typeof page === 'number' ? page - 1 : 0,
        size: searchParams?.size,
        startDate: searchParams?.startDate ?? '',
        endDate: searchParams?.endDate ?? '',
        status: searchParams?.status as
          | 'RECEIVED'
          | 'PROCESSING'
          | 'CLEANSED'
          | 'VALIDATION_ERROR'
          | 'USER_VERIFIED'
          | 'COMPLETED',
        categoryId: searchParams?.category ? parseInt(searchParams?.category) : undefined,
        searchFieldName: searchParams?.searchKey ?? '',
        searchKeyword: searchParams?.searchValue ?? '',
        sort: searchParams?.sort ? [searchParams.sort] : undefined,
      })
      .then((res) => {
        if (res.status === 200) {
          setOrderCleansings(res.data);
          orderCleansingRef.current = res.data;
          setPaginationInfo(usePaginationHeader(res.headers));
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    ServiceHelper.reset();
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const category = searchParams.get('category');
    const status = searchParams.get('status');
    const size = searchParams.get('size');
    const page = searchParams.get('page');
    const searchKey = searchParams.get('searchKey');
    const searchValue = searchParams.get('searchValue');
    const sort = searchParams.get('sort');

    if (page !== null) {
      setPage(parseInt(page));
    } else {
      setPage(1);
    }

    fetchOrderCleansings(page ? parseInt(page) : 1, {
      startDate: startDate ?? '',
      endDate: endDate ?? '',
      category: category ?? '',
      status: status ?? '',
      size: size ? parseInt(size) : 50,
      page: page ? parseInt(page) : 1,
      searchKey: searchKey ?? '',
      searchValue: searchValue ?? '',
      sort: sort ?? '',
    });
  }, [location.search]);

  return (
    <>
      <Row justifyContent="space-between" padding="0 20px">
        <FilterBox onSearch={handleSearch} />
        <Row gap={5} paddingTop="10px">
          <RecordText>{paginationInfo?.totalCount?.toLocaleString()}</RecordText>
          <div>{t('count')}</div>
          <IconButton onClick={handleSettingsClick}>
            <Badge
              badgeContent="!"
              color={isSettingsChange === 'EXIST_HIDE' ? 'warning' : 'error'}
              invisible={isSettingsChange === 'ALL_SHOW'}
            >
              <Settings />
            </Badge>
          </IconButton>
        </Row>
      </Row>
      {!loading && (orderCleansings.length === 0 || isSettingsChange === 'ALL_HIDE') ? (
        <NoContentStyle>
          <div>
            {isSettingsChange === 'ALL_HIDE' ? t('All Invisible Columns') : t('No data found')}
          </div>
        </NoContentStyle>
      ) : (
        <TableWrapper ref={tableRef}>
          <CommonTable
            pagination={paginationInfo?.currentPage}
            handleRowClick={handleRowClick}
            handleDoneClick={(row: OrderCleansingDto) => {
              handleDoneClick(
                row.id as number,
                row.customsTitle || row.recommendTitlePrimary || '',
                false,
                undefined,
              );
            }}
            data={orderCleansings}
            onSortingChange={handleSortingChange}
            externalSorting={sorting}
            visibleColumnItems={visibleColumnItems}
          />

          <PaginationWrapper>
            <PaginationWrapperInner>
              <Row gap={20}>
                <Pagination
                  color="primary"
                  count={paginationInfo?.totalPages}
                  page={page || 1}
                  onChange={handleChange}
                />
              </Row>
            </PaginationWrapperInner>
          </PaginationWrapper>
        </TableWrapper>
      )}

      <OverlayStyle show={loading}>
        <SyncLoader color="#930000" size={10} />
      </OverlayStyle>
    </>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  padding: 10px 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  position: fixed;
  bottom: 10px;

  z-index: 2;

  left: 0;
  right: 0;
  margin: auto;
`;

const PaginationWrapperInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  /* box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3); */
  background: #fff;
  border-radius: 20px;
  padding: 10px;
`;


const RecordText = styled.div`
  color: #6e00c7;
  text-align: center;
  font-family: Ubuntu;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px; /* 131.25% */
`;

const NoContentStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 200px);
  > div {
    padding: 100px;
    border: 1px solid #000;
    border-radius: 10px;
  }
`;

export default TitleClensingPage;
